import type { TourConfig } from '../types';

export const gettingStartedTour: TourConfig = {
  id: 'getting-started',
  name: 'Getting Started',
  description: 'Learn the basics of Solidafy — from data sources to pipelines',
  steps: [
    {
      target: '[data-tour="sidebar-ingestion"]',
      title: 'Ingestion',
      content:
        'This is where all your data ingestion lives. Data sources, pipelines, and transforms — everything you need to bring data into Solidafy.',
      placement: 'right',
      featureKey: ['ingestion.data_sources', 'ingestion.pipelines', 'ingestion.transforms'],
      roleContent: {
        analyst: {
          content:
            "This section shows your organization's data ingestion setup. Your engineering team uses data sources, pipelines, and transforms to bring data into Solidafy.",
        },
        viewer: {
          content:
            "This section shows your organization's data ingestion setup. Your engineering team uses data sources, pipelines, and transforms to bring data into Solidafy.",
        },
      },
    },
    {
      target: '[data-tour="sidebar-datasources"]',
      title: 'Data Sources',
      content:
        'Data sources are your connections to external systems — databases, APIs, SaaS tools like GitHub, HubSpot, Jira, and more. Each data source uses a connector to pull data in.',
      placement: 'right',
      navigateTo: '/data-sources',
      waitForTarget: true,
      featureKey: 'ingestion.data_sources',
      roleContent: {
        analyst: {
          content:
            'Data sources are the external systems your team has connected — databases, APIs, SaaS tools like GitHub, HubSpot, Jira, and more. Each one syncs data into Solidafy automatically.',
        },
        viewer: {
          content:
            'Data sources are the external systems your team has connected — databases, APIs, SaaS tools like GitHub, HubSpot, Jira, and more. Each one syncs data into Solidafy automatically.',
        },
      },
    },
    {
      target: '[data-tour="add-datasource-btn"]',
      title: 'Add a Data Source',
      content:
        "Click here to connect a new data source. You'll pick a connector type (e.g. PostgreSQL, GitHub), then configure the connection details and credentials.",
      placement: 'bottom',
      navigateTo: '/data-sources',
      waitForTarget: true,
      featureKey: 'ingestion.data_sources',
      roleContent: {
        analyst: {
          title: 'Connecting Data Sources',
          content:
            'Your engineering team uses this to connect new data sources — databases, APIs, SaaS tools, and more. You can browse all connected sources and check their sync status.',
        },
        viewer: {
          title: 'Connecting Data Sources',
          content:
            'Your engineering team uses this to connect new data sources — databases, APIs, SaaS tools, and more. You can browse all connected sources and check their sync status.',
        },
      },
    },
    {
      target: '[data-tour="datasource-list"]',
      title: 'Your Data Sources',
      content:
        'Once connected, your data sources appear here. You can see their sync status, how many streams (tables) they have, and which pipeline they belong to.',
      placement: 'bottom',
      navigateTo: '/data-sources',
      waitForTarget: true,
      featureKey: 'ingestion.data_sources',
      roleContent: {
        analyst: {
          content:
            'Your connected data sources are listed here. Check their sync status, see how many streams (tables) each one has, and trace which pipeline they belong to.',
        },
        viewer: {
          content:
            'Your connected data sources are listed here. Check their sync status, see how many streams (tables) each one has, and trace which pipeline they belong to.',
        },
      },
    },
    {
      target: '[data-tour="sidebar-pipelines"]',
      title: 'Pipelines',
      content:
        'Pipelines orchestrate your data flow. They combine data sources with transforms and run them on a schedule — or manually. Think of them as your data assembly line.',
      placement: 'right',
      featureKey: 'ingestion.pipelines',
      roleContent: {
        analyst: {
          content:
            'Pipelines show how data moves from sources into your data lake on a schedule. Your engineering team configures these to keep everything up to date — you can track their runs here.',
        },
        viewer: {
          content:
            'Pipelines show how data moves from sources into your data lake on a schedule. Your engineering team configures these to keep everything up to date — you can track their runs here.',
        },
      },
    },
    {
      target: '[data-tour="sidebar-catalog"]',
      title: 'Data Catalog',
      content:
        'After your pipelines run, data lands in the catalog. Browse namespaces, tables, and schemas. This is your data lake — all queryable and explorable.',
      placement: 'right',
      featureKey: 'data_lake.catalog',
      // Catalog is equally relevant for all roles — no override needed
    },
    {
      target: '[data-tour="sidebar-explorer"]',
      title: 'Data Explorer',
      content:
        'Use the explorer to query your data directly using Solidafy DSL. Build queries visually, see results in tables or charts, and export what you need.',
      placement: 'right',
      featureKey: 'data_lake.explorer',
      // Explorer is equally relevant for all roles — no override needed
    },
    {
      target: '[data-tour="sidebar-dashboards"]',
      title: 'Dashboards',
      content:
        'Create visual dashboards from your data. KPIs, charts, tables — all powered by your data lake. Share them with your team or embed them in products.',
      placement: 'right',
      featureKey: 'analytics.dashboards',
      roleContent: {
        analyst: {
          content:
            'Browse dashboards your team has built from the data lake. KPIs, charts, and tables give you quick insight into your key metrics — and you can build your own too.',
        },
        viewer: {
          content:
            'Browse dashboards your team has built from the data lake. KPIs, charts, and tables give you quick insight into your key metrics.',
        },
      },
    },
    {
      target: '[data-tour="sidebar-settings"]',
      title: 'Settings & API Keys',
      content:
        'Configure your organization, manage environments, generate API keys for agents, and set up notifications. Your credentials and encryption keys live here too.',
      placement: 'right',
      featureKey: [
        'settings.app_settings',
        'settings.api_keys',
        'settings.notifications',
        'settings.organization',
        'settings.environments',
        'settings.security_groups',
        'settings.sso',
      ],
      roleContent: {
        analyst: {
          title: 'Settings',
          content:
            'Find your personal API keys and notification preferences here. Organization-wide settings and environment configuration are managed by your admin team.',
        },
        viewer: {
          title: 'Settings',
          content:
            'Find your personal API keys and notification preferences here. Organization-wide settings and environment configuration are managed by your admin team.',
        },
      },
    },
    {
      target: '[data-tour="env-selector"]',
      title: 'Environments',
      content:
        'Switch between environments (dev, staging, production) here. Each environment has its own data lake, credentials, and agents — completely isolated.',
      placement: 'bottom',
      // Environments are equally relevant for all roles — no override needed
    },
  ],
};
